import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
// import {LOOK_SENSITIVITY} from "./constants.js";
// import { FaArrowUp } from 'react-icons/fa';
import {TbArrowBounce} from "react-icons/tb";

const Container = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none; /* Container itself doesn't capture events */
    z-index: 10;
    overflow: hidden; /* Prevent potential scroll issues */
`;

const JoystickArea = styled.div`
    position: absolute;
    bottom: 20px;
    left: 20px;
    width: 120px;
    height: 120px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto; /* This area captures its own events */
    touch-action: none; /* Prevent default browser actions like scrolling */
    user-select: none; /* Prevent text selection */
    -webkit-user-drag: none; /* Prevent dragging */
    -webkit-tap-highlight-color: transparent; /* Remove tap highlight */
`;

const Joystick = styled.div`
    width: 50px;
    height: 50px;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 25px;
    position: relative;
    left: ${props => props.x}px;
    top: ${props => props.y}px;
    transition: ${props => props.$active ? 'none' : 'all 0.2s'};
`;

// LookArea covers the whole screen
const LookArea = styled.div`
    position: absolute; top: 0; left: 0; width: 100%; height: 100%;
    pointer-events: auto; touch-action: none; user-select: none;
    -webkit-user-drag: none; -webkit-tap-highlight-color: transparent;
    /* background: rgba(0, 255, 0, 0.05); */ /* Optional Debug */
`;

const JumpButton = styled.button`
    position: absolute;
    bottom: 40px;
    right: 40px;
    width: 80px;
    height: 80px;
    border-radius: 40px;
    background: rgba(255, 255, 255, 0.3);
    border: 2px solid white;
    color: white; /* Icon will inherit this color */
    /* font-size: 16px; */ /* No longer needed for text */
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-drag: none;
    -webkit-tap-highlight-color: transparent;

    /* Add a subtle effect on press */
    &:active {
        background: rgba(255, 255, 255, 0.5);
    }
`;

const MobileControls = ({ onMove, onJump, onRotate, onZoom }) => {
    const [joystickPos, setJoystickPos] = useState({ x: 0, y: 0 });
    const [joystickActive, setJoystickActive] = useState(false);
    const joystickAreaRef = useRef(null);

    // --- Refs for Touch State ---
    // Store active touches NOT associated with the joystick
    const activeLookTouches = useRef(new Map()); // Map<touchId, { x, y }>
    const isPinching = useRef(false);
    const initialPinchDistance = useRef(0);
    const pinchStateRef = useRef({ lastDistance: 0 }); // Ref to store pinch state like last distance
    const lastLookPositionRef = useRef(null); // Ref to store the last position for single-finger look

    // --- Helper: Calculate distance ---
    const getDistance = (touch1, touch2) => {
        const dx = touch1.x - touch2.x;
        const dy = touch1.y - touch2.y;
        return Math.sqrt(dx * dx + dy * dy);
    };

    // --- Joystick Handlers ---
    // (Keep these exactly as they were in the previous attempt, including stopPropagation)
    const handleJoystickStart = (e) => {
        e.stopPropagation();
        //e.preventDefault();
        if (joystickActive === false && e.touches.length > 0) { // Check internal state too
            const touch = e.touches[0];
            joystickAreaRef.current.touchId = touch.identifier; // Store ID on ref
            setJoystickActive(true);
            updateJoystickPosition(touch);
            console.log("Joystick Start:", touch.identifier);
        }
    };
    const handleJoystickMove = (e) => {
        e.stopPropagation();
        //e.preventDefault();
        if (joystickActive === true && joystickAreaRef.current.touchId !== undefined) {
            for (let i = 0; i < e.touches.length; i++) {
                if (e.touches[i].identifier === joystickAreaRef.current.touchId) {
                    updateJoystickPosition(e.touches[i]);
                    return;
                }
            }
        }
    };
    const handleJoystickEnd = (e) => {
        e.stopPropagation();
        for (let i = 0; i < e.changedTouches.length; i++) {
            if (e.changedTouches[i].identifier === joystickAreaRef.current.touchId) {
                console.log("Joystick End:", joystickAreaRef.current.touchId);
                joystickAreaRef.current.touchId = undefined;
                setJoystickActive(false);
                setJoystickPos({ x: 0, y: 0 });
                onMove(0, 0, 0);
                break;
            }
        }
    };

    const updateJoystickPosition = (touch) => {
        if (!joystickAreaRef.current) return;
        const rect = joystickAreaRef.current.getBoundingClientRect();
        const centerX = rect.width / 2; const centerY = rect.height / 2;
        let x = touch.clientX - rect.left - centerX;
        let y = touch.clientY - rect.top - centerY;
        const distance = Math.sqrt(x * x + y * y);
        const maxDistance = centerX - 25;
        if (distance > maxDistance) {
            x = (x / distance) * maxDistance; y = (y / distance) * maxDistance;
        }
        setJoystickPos({ x, y });
        const moveX = x / maxDistance;
        const moveZ = y / maxDistance;
        onMove(moveX, moveZ, 0);
    };

    // --- Look/Zoom Area Handlers (Simpler Logic) ---
    const handleTouchStart = (e) => {
        //e.preventDefault();
        const touches = e.changedTouches; // Focus on touches starting *now*
        for (let i = 0; i < touches.length; i++) {
            const touch = touches[i];
            // Ignore if this touch started in the joystick area
            const joyStickRect = joystickAreaRef.current?.getBoundingClientRect();
            if (joyStickRect &&
                touch.clientX >= joyStickRect.left && touch.clientX <= joyStickRect.right &&
                touch.clientY >= joyStickRect.top && touch.clientY <= joyStickRect.bottom) {
                console.log(`Touch ${touch.identifier} started in joystick, ignoring in LookArea`);
                continue;
            }

            // Add new touch to our map
            activeLookTouches.current.set(touch.identifier, { x: touch.clientX, y: touch.clientY });
            console.log(`LookArea Start: Added touch ${touch.identifier}. Total active: ${activeLookTouches.current.size}`);
        }
        updateInteractionState();
    };

    const handleTouchMove = (e) => {
        //e.preventDefault();
        const touches = e.changedTouches; // Focus on touches that moved
        let lookMoved = false;
        let pinchMoved = false;

        // Update positions of moved touches
        for (let i = 0; i < touches.length; i++) {
            const touch = touches[i];
            if (activeLookTouches.current.has(touch.identifier)) {
                activeLookTouches.current.set(touch.identifier, { x: touch.clientX, y: touch.clientY });
                // Determine if this move contributes to look or pinch
                if (!isPinching.current && activeLookTouches.current.size === 1) {
                    lookMoved = true;
                } else if (isPinching.current && activeLookTouches.current.size === 2) {
                    pinchMoved = true;
                }
            }
        }

        // Process interactions based on current state
        if (isPinching.current && pinchMoved) {
            // --- Pinch Zoom Calculation ---
            const touchArray = Array.from(activeLookTouches.current.values());
            const currentDist = getDistance(touchArray[0], touchArray[1]);
            // Use lastDistance from state ref for smoother relative zoom frame-to-frame
            const lastDist = pinchStateRef.current.lastDistance || initialPinchDistance.current; // Fallback to initial if needed

            console.log(`Pinch Move: currentDist: ${currentDist.toFixed(1)}, lastDist: ${lastDist.toFixed(1)}`);

            if (lastDist > 0) { // Avoid division by zero
                const distChangeRatio = currentDist / lastDist;
                // Convert ratio change to an additive delta for onZoom.
                // A ratio > 1 means zoom out (positive delta), < 1 means zoom in (negative delta).
                // The scaling factor (e.g., * 2.0) adjusts sensitivity.
                const approxZoomDelta = (distChangeRatio - 1.0) * 2.0; // Adjust sensitivity multiplier as needed

                console.log(`Pinch Move: Ratio: ${distChangeRatio.toFixed(3)}, ApproxDelta: ${approxZoomDelta.toFixed(4)}`);

                if (Math.abs(approxZoomDelta) > 0.005) { // Threshold to prevent jitter
                    onZoom(approxZoomDelta);
                    // Update last distance in the state ref for the next move event
                    pinchStateRef.current.lastDistance = currentDist;
                }
            }

        } else if (!isPinching.current && lookMoved) {
            // --- Look Rotation Calculation ---
            // Ensure we only process look if exactly one finger is active
            if (activeLookTouches.current.size === 1) {
                const touchId = activeLookTouches.current.keys().next().value;
                const currentTouch = activeLookTouches.current.get(touchId);
                const lastPos = lastLookPositionRef.current; // Use the dedicated ref for previous position

                if (lastPos) { // Check if we have a previous position to compare against
                    const deltaX = currentTouch.x - lastPos.x;
                    const deltaY = currentTouch.y - lastPos.y;

                    if (Math.abs(deltaX) > 0.1 || Math.abs(deltaY) > 0.1) { // Add a small movement threshold
                        console.log(`Look Move: Raw dX: ${deltaX.toFixed(1)}, Raw dY: ${deltaY.toFixed(1)}`);

                        // *** CHANGE HERE: Pass raw deltas ***
                        // Sensitivity will be applied in the GameScreen component
                        onRotate(deltaX, deltaY);

                        lastLookPositionRef.current = { x: currentTouch.x, y: currentTouch.y };
                    }
                } else {
                    // If lastPos is null, it means this is the first 'move' event after touchstart,
                    // so just record the current position as the 'last' for the *next* move event.
                    lastLookPositionRef.current = { x: currentTouch.x, y: currentTouch.y };
                }
            }
        }
    };

    const handleTouchEnd = (e) => {
        e.preventDefault();
        const touches = e.changedTouches; // Focus on touches ending *now*
        for (let i = 0; i < touches.length; i++) {
            const touch = touches[i];
            if (activeLookTouches.current.has(touch.identifier)) {
                activeLookTouches.current.delete(touch.identifier);
                console.log(`LookArea End: Removed touch ${touch.identifier}. Total active: ${activeLookTouches.current.size}`);
            }
        }
        // Reset last look position immediately if no fingers are left for looking
        if (activeLookTouches.current.size < 1) {
            lastLookPositionRef.current = null;
        }
        // Update pinch/look state based on remaining touches
        updateInteractionState();
    };

    // Helper function to determine interaction state based on active touches
    // This should be called AFTER touches are added/removed in start/end handlers
    const updateInteractionState = () => {
        const touchCount = activeLookTouches.current.size;

        // --- Transitioning TO Pinch ---
        if (touchCount === 2 && !isPinching.current) {
            isPinching.current = true;
            const touchArray = Array.from(activeLookTouches.current.values());
            initialPinchDistance.current = getDistance(touchArray[0], touchArray[1]);
            pinchStateRef.current.lastDistance = initialPinchDistance.current; // Initialize last distance for move calculation
            lastLookPositionRef.current = null; // Stop look rotation
            console.log("Interaction State: Start Pinching. Initial Dist:", initialPinchDistance.current.toFixed(1));
        }
        // --- Transitioning FROM Pinch ---
        else if (touchCount < 2 && isPinching.current) {
            isPinching.current = false;
            initialPinchDistance.current = 0;
            pinchStateRef.current.lastDistance = 0; // Reset pinch distance tracking
            console.log("Interaction State: Stop Pinching.");
            // If exactly one finger remains, immediately set its position as the start for looking
            if (touchCount === 1) {
                const remainingTouch = activeLookTouches.current.values().next().value;
                lastLookPositionRef.current = { x: remainingTouch.x, y: remainingTouch.y };
                console.log("Interaction State: Transitioned Pinch->Look. Set lastLookPos.");
            } else {
                lastLookPositionRef.current = null; // No fingers left
            }
        }
        // --- Handling Start of Single Touch ---
        else if (touchCount === 1 && !isPinching.current && !lastLookPositionRef.current) {
            // If we just started the first touch (and not coming from pinch end), record its position
            const firstTouch = activeLookTouches.current.values().next().value;
            lastLookPositionRef.current = { x: firstTouch.x, y: firstTouch.y };
            console.log("Interaction State: Started single touch look.");
        }
        console.log(`Interaction State Update: Count=${touchCount}, Pinching=${isPinching.current}`);
    };


    // --- Jump Button Handler ---
    const handleJumpTouchStart = (e) => {
        // Prevent LookArea or other handlers from processing this tap
        e.stopPropagation();
        e.preventDefault(); // Prevent default button actions
        console.log("Jump Pressed");
        onJump();
    };

    return (
        <Container>
            {/* Look/Zoom area covers everything */}
            <LookArea
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd} // Treat cancel like end
            />

            {/* Joystick sits on top visually and handles its own events */}
            <JoystickArea
                ref={joystickAreaRef}
                onTouchStart={handleJoystickStart}
                onTouchMove={handleJoystickMove}
                onTouchEnd={handleJoystickEnd}
                onTouchCancel={handleJoystickEnd} // Treat cancel like end
            >
                <Joystick x={joystickPos.x} y={joystickPos.y} $active={joystickActive} />
            </JoystickArea>

            {/* Jump button sits on top visually */}
            <JumpButton
                onTouchStart={handleJumpTouchStart}
                // Add onClick as fallback for non-touch devices if needed, but prevent default
                onClick={(e) => e.preventDefault()}
            >
                <TbArrowBounce size={30} /> {/* Adjust size as needed */}
            </JumpButton>
        </Container>
    );
};

export default MobileControls;
